import { faCaretRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { fetchLangualRootDescriptors, fetchLanguaLChildren, searchLanguaL } from '@/api/langual'
import { SearchField } from '@/components/SearchField'
import { Spinner } from '@/components/Spinner'
import LanguaLUtils, { LanguaLDescriptor, LanguaLID } from './LanguaLUtils'

import './langual.css'

type LanguaLSelectorProps = {
  includeSubtree?: boolean
  selectedDescriptorIDs: Array<LanguaLID>
  enableSearch?: boolean
  onChangeSelection: (ids: Array<LanguaLID>, descriptor?: LanguaLDescriptor) => Promise<void> | void
  onChangeIncludeTree: (include: boolean) => void
  isLoadingNodeID?: number | boolean
}

function updateNodeChildren(
  nodes: Array<LanguaLDescriptor>,
  targetId: LanguaLID,
  children: Array<LanguaLDescriptor>,
): Array<LanguaLDescriptor> {
  return nodes.map((n) =>
    n.id === targetId
      ? { ...n, childrenDescriptors: children }
      : {
          ...n,
          childrenDescriptors:
            n.childrenDescriptors && updateNodeChildren(n.childrenDescriptors, targetId, children),
        },
  )
}

export default function LanguaLSelector(props: LanguaLSelectorProps) {
  const { selectedDescriptorIDs, includeSubtree, enableSearch } = props

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [descriptors, setDescriptors] = useState<Array<LanguaLDescriptor>>([])
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [searchText, setSearchText] = useState<string>('')
  const [searchResults, setSearchResults] = useState<Array<LanguaLDescriptor>>([])
  const [isLoadingNodeID, setIsLoadingNodeID] = useState<LanguaLID | false>(false)

  // cache for children inside the component as well (works with Utils cache, avoids double renders)
  const childrenCacheRef = useRef<Map<string, Array<LanguaLDescriptor>>>(new Map())
  const searchSeqRef = useRef<number>(0)

  useEffect(function loadRoots() {
    let mounted = true
    setIsLoading(true)
    //LanguaLUtils.loadCaches()
    fetchLangualRootDescriptors().then((root) => {
      if (!mounted) return
      // Pre-expand & merge parents for preselected IDs (if any)
      LanguaLUtils.loadLeafParentsOnRootDescriptors(selectedDescriptorIDs, root).then(
        ({ rootDescriptors, expandedParents }) => {
          if (!mounted) return
          setDescriptors(rootDescriptors)
          setExpanded(new Set(expandedParents.map((d) => d.id)))
          setIsLoading(false)
        },
      )
    })
    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onToggleSubtree = useCallback(
    function onToggleSubtree() {
      props.onChangeIncludeTree(!includeSubtree)
    },
    [props, includeSubtree],
  )

  async function loadChildren(id: LanguaLID): Promise<Array<LanguaLDescriptor>> {
    const key = id
    if (childrenCacheRef.current.has(key)) return childrenCacheRef.current.get(key)!
    // also warms the Utils cache
    const children = await fetchLanguaLChildren(id)
    childrenCacheRef.current.set(key, children)
    return children
  }

  const onClickExpandNode = useCallback(
    function onClickExpandNode(descriptor: LanguaLDescriptor) {
      const key = descriptor.id
      const currentlyExpanded = expanded.has(key)
      if (currentlyExpanded) {
        const next = new Set(expanded)
        next.delete(key)
        setExpanded(next)
        return
      }

      // expanding: ensure children loaded once
      if (!descriptor.childrenDescriptors) {
        setIsLoading(true)
        loadChildren(descriptor.id).then((children) => {
          setDescriptors((prev) => updateNodeChildren(prev, descriptor.id, children))
          setIsLoading(false)
          setExpanded((prev) => {
            const next = new Set(prev)
            next.add(key)
            return next
          })
        })
      } else {
        setExpanded((prev) => {
          const next = new Set(prev)
          next.add(key)
          return next
        })
      }
    },
    [expanded],
  )

  const onToggleNodeCheckbox = useCallback(
    function onToggleNodeCheckbox(descriptor: LanguaLDescriptor) {
      const key = descriptor.id
      setIsLoadingNodeID(key)
      const next =
        selectedDescriptorIDs.findIndex((d) => d === key) >= 0
          ? selectedDescriptorIDs.filter((d) => d !== key)
          : [...selectedDescriptorIDs, key]

      Promise.resolve(props.onChangeSelection(next, descriptor)).finally(() => {
        setIsLoadingNodeID(false)
      })
    },
    [props, selectedDescriptorIDs],
  )

  // Debounced search
  useEffect(
    function handleSearch() {
      const text = searchText.trim()
      const seq = ++searchSeqRef.current
      setExpanded(new Set())

      if (!enableSearch) return
      if (text.length < 2) {
        setSearchResults([])
        return
      }

      const timer = setTimeout(() => {
        setIsLoading(true)
        searchLanguaL(text).then((results) => {
          // ignore stale results
          if (seq !== searchSeqRef.current) return
          LanguaLUtils.loadLeafParentsOnRootDescriptors(
            results.map((r) => r.id),
            descriptors,
          ).then(({ rootDescriptors, expandedParents }) => {
            if (seq !== searchSeqRef.current) return
            setDescriptors(rootDescriptors)
            setSearchResults(results)
            setExpanded((prev) => {
              const next = new Set(prev)
              expandedParents.forEach((p) => next.add(p.id))
              return next
            })
            setIsLoading(false)
          })
        })
      }, 250)

      return () => clearTimeout(timer)
    },
    // include descriptors so that merging happens over latest tree
    [searchText, enableSearch],
  )

  const renderDescriptorTree = useCallback(
    function renderDescriptorTree(nodes: Array<LanguaLDescriptor>): React.ReactNode {
      return nodes.map((descriptor) => {
        const isSelected = selectedDescriptorIDs.findIndex((d) => d === descriptor.id) >= 0
        const isSearchResult = searchResults.findIndex((d) => d.id === descriptor.id) >= 0

        const searchResultClass = isSearchResult ? 'is-search-result' : ''
        const descriptorClass =
          descriptor.childrenDescriptors && descriptor.childrenDescriptors.length === 0
            ? 'noChildren'
            : expanded.has(descriptor.id)
              ? 'expanded'
              : 'collapsed'

        return (
          <li
            key={descriptor.id}
            className={`filterTree-item ${descriptorClass} ${searchResultClass}`}>
            <div className={`filterTree-itemSelf ${isSelected ? 'is-selected' : ''}`}>
              <span
                className="filterTree-expandButton"
                onClick={() => onClickExpandNode(descriptor)}
              />
              <span
                className="filterTree-itemDesc"
                onClick={() => onToggleNodeCheckbox(descriptor)}>
                {LanguaLUtils.capitalizeText(
                  descriptor.translatedDescription || descriptor.description,
                )}
              </span>
              {isLoadingNodeID === descriptor.id ? (
                <div className="filterTree-itemLoading">
                  <Spinner />
                </div>
              ) : null}
              <input
                className="filterTree-itemCheckbox"
                type="checkbox"
                checked={isSelected}
                value={descriptor.id}
                onChange={() => onToggleNodeCheckbox(descriptor)}
              />
            </div>
            <div className="filterTree-itemChildren">
              {descriptor.childrenDescriptors && descriptor.childrenDescriptors.length > 0 ? (
                <ul className="languaLTreeWidget">
                  {renderDescriptorTree(descriptor.childrenDescriptors)}
                </ul>
              ) : null}
            </div>
          </li>
        )
      })
    },
    [
      expanded,
      isLoadingNodeID,
      onClickExpandNode,
      onToggleNodeCheckbox,
      searchResults,
      selectedDescriptorIDs,
    ],
  )

  const groupList = useMemo(() => descriptors ?? [], [descriptors])

  return (
    <div className={'languaLSelector' + (includeSubtree ? ' includeSubtree' : '')}>
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <div className="filterActions">
            {enableSearch ? (
              <SearchField defaultValue={searchText} onChange={setSearchText} />
            ) : null}
            <label>
              Include Subtree
              <input type="checkbox" checked={!!includeSubtree} onChange={onToggleSubtree} />
            </label>
          </div>
          <ul className="filterTree-groupList">
            {groupList.map((descriptor) => {
              const isExpanded = expanded.has(descriptor.id)
              return (
                <li
                  className={`filterTree-group ${isExpanded ? 'is-expanded' : ''}`}
                  key={descriptor.id}>
                  <span className="filterTree-toggle" onClick={() => onClickExpandNode(descriptor)}>
                    {LanguaLUtils.capitalizeText(
                      descriptor.translatedDescription || descriptor.description,
                    )}
                    <FontAwesomeIcon icon={faCaretRight} />
                  </span>
                  {descriptor.childrenDescriptors && descriptor.childrenDescriptors.length > 0 ? (
                    <ul className="languaLTreeWidget">
                      {renderDescriptorTree(descriptor.childrenDescriptors)}
                    </ul>
                  ) : null}
                </li>
              )
            })}
          </ul>
        </>
      )}
    </div>
  )
}
